
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCard, TrendingUp, Clock, CheckCircle2, AlertCircle, DollarSign } from 'lucide-react';
import { Payment } from '@/services/types';

interface PaymentStatsCardProps {
  payments: Payment[];
  totalCount: number;
  loading?: boolean;
}

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  format?: (value: number) => string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 1000, 
  format = (v) => v.toString() 
}) => {
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{format(displayValue)}</span>;
};

export const PaymentStatsCard: React.FC<PaymentStatsCardProps> = ({ 
  payments, 
  totalCount, 
  loading = false 
}) => {
  const stats = React.useMemo(() => {
    const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);
    const completedPayments = payments.filter(p => p.status === 'completed' || p.status === 'paid').length;
    const pendingPayments = payments.filter(p => p.status === 'pending').length;
    const errorPayments = payments.filter(p => p.status === 'error').length;
    const authPayments = payments.filter(p => p.status === 'auth').length;

    const avgAmount = payments.length > 0 ? totalAmount / payments.length : 0;

    return {
      totalAmount,
      completedPayments,
      pendingPayments,
      errorPayments,
      authPayments,
      avgAmount,
      completionRate: payments.length > 0 ? (completedPayments / payments.length) * 100 : 0
    };
  }, [payments]);

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('ru-RU', { 
      style: 'currency', 
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);

  const formatNumber = (num: number) => 
    new Intl.NumberFormat('ru-RU').format(num);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Статистика платежей
          </CardTitle>
          <CardDescription>Загрузка статистики...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
                <div className="h-6 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Статистика платежей
        </CardTitle>
        <CardDescription>
          Общая информация по {formatNumber(totalCount)} платежам
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Общая сумма */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              Общая сумма
            </div>
            <div className="text-2xl font-bold text-green-600">
              <AnimatedCounter 
                value={stats.totalAmount} 
                format={formatCurrency}
                duration={1500}
              />
            </div>
          </div>

          {/* Выполненные платежи */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              Выполнено
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-600">
                <AnimatedCounter value={stats.completedPayments} />
              </span>
              <Badge variant="outline" className="text-green-600 border-green-200">
                <AnimatedCounter 
                  value={stats.completionRate} 
                  format={(v) => `${v.toFixed(1)}%`}
                />
              </Badge>
            </div>
          </div>

          {/* Ожидающие платежи */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              В ожидании
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-orange-600">
                <AnimatedCounter value={stats.pendingPayments} />
              </span>
              {stats.authPayments > 0 && (
                <Badge variant="secondary">
                  +<AnimatedCounter value={stats.authPayments} /> авториз.
                </Badge>
              )}
            </div>
          </div>

          {/* Средний чек */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Средний чек
            </div>
            <div className="text-2xl font-bold text-blue-600">
              <AnimatedCounter 
                value={stats.avgAmount} 
                format={formatCurrency}
                duration={1200}
              />
            </div>
            {stats.errorPayments > 0 && (
              <div className="flex items-center gap-1 text-red-600 text-xs">
                <AlertCircle className="h-3 w-3" />
                <AnimatedCounter value={stats.errorPayments} /> ошибок
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
